// The mosaic gallery is a full-screen sheet on the breakpoints where it slides in (see
// _gallery.scss). Hiding it is a bare class removal, so on the way out the sheet would vanish on
// the spot instead of sliding back: it has to stay mounted until its animation has finished.

const SHEET_CLOSING_CLASS = 'mosaicPopup--closing';
const OVERLAY_CLOSING_CLASS = 'mosaicPopupOverlay--closing';
const SHEET_NO_ANIMATION_CLASS = 'ds-modal-wrapper--noAnimation';
const OVERLAY_NO_ANIMATION_CLASS = 'ds-modal-overlay--noAnimation';

// Mirrors $for-intermediate, the breakpoint the slide animations are scoped to in _gallery.scss.
const SLIDE_MEDIA_QUERY = '(max-width: 991.98px)';

// A missed animationend would leave the sheet mounted and the body stuck at overflow-hidden, so the
// slide-down gets a deadline rather than being left to a single event.
const SLIDE_DOWN_TIMEOUT = 300;

// Every condition the closing animation is scoped to in CSS has to be repeated here. Deferring the
// hide when no animation will actually run leaves the sheet on screen until the deadline fires.
const isSlideAnimated = () => window.matchMedia(SLIDE_MEDIA_QUERY).matches
  && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function withSlideTransition(modal) {
  const sheet = modal.wrapperElement;
  const overlay = modal.overlayElement;

  // Set only while a slide-down is in flight. Calling it undoes the close - the deadline, the
  // listener and the classes - so a re-open can call off the hide that is still pending against
  // the gallery the viewer has just brought back.
  let abortClose = null;

  // Toggled only while the sheet is off screen: swapping the animation of a sheet that is already
  // showing would restart its entrance.
  const setEntranceAnimated = (animate) => {
    sheet.classList.toggle(SHEET_NO_ANIMATION_CLASS, !animate);
    overlay.classList.toggle(OVERLAY_NO_ANIMATION_CLASS, !animate);
  };

  // animate: false is for the browser's own back/forward (e.g. iOS edge swipes), which already slide
  // the page in or out - a second slide on top of that reads as the gallery jumping.
  const show = ({ animate = true } = {}) => {
    const isOnScreen = modal.isVisible() && !abortClose;

    abortClose?.();
    if (!isOnScreen) {
      setEntranceAnimated(animate);
    }
    modal.show();
  };

  const hide = ({ animate = true } = {}) => {
    if (abortClose) {
      return;
    }

    if (!animate || !modal.isVisible() || !isSlideAnimated(sheet)) {
      modal.hide();
      return;
    }

    const closeNow = () => {
      abortClose();
      modal.hide();
    };

    // The re-opened sheet runs its own entrance animation on this same element, so an interrupted
    // close has to stop listening rather than treat that as its slide-down finishing.
    const onSlideDownEnd = (event) => {
      if (event.target === sheet) {
        closeNow();
      }
    };

    const timeoutId = window.setTimeout(closeNow, SLIDE_DOWN_TIMEOUT);

    abortClose = () => {
      abortClose = null;
      window.clearTimeout(timeoutId);
      sheet.removeEventListener('animationend', onSlideDownEnd);
      sheet.classList.remove(SHEET_CLOSING_CLASS);
      overlay.classList.remove(OVERLAY_CLOSING_CLASS);
    };

    sheet.addEventListener('animationend', onSlideDownEnd);
    sheet.classList.add(SHEET_CLOSING_CLASS);
    overlay.classList.add(OVERLAY_CLOSING_CLASS);
  };

  return {
    show,
    hide,
    isVisible: () => modal.isVisible(),
  };
}
